import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import SaveBar from '@/components/forms/SaveBar'
import { useIsReadOnly, type AppAbility } from '@/lib/casl'
import { serverFieldErrors, useAppForm } from '@/lib/form'
import {
  googleTagManagerQueries,
  useRemoveGoogleTagManagerIntegrationMutation,
  useSuspenseGoogleTagManagerIntegration,
  useUpdateGoogleTagManagerIntegrationMutation,
} from '@/lib/services/google-tag-manager/google-tag-manager.query'
import {
  googleTagManagerIntegrationSchema,
  normalizeGoogleTagManagerContainerId,
} from '@/lib/services/google-tag-manager/google-tag-manager.schema'
import { getErrorMessage } from '@/lib/utils'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { ExternalLink, Tags, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

const PAGE_DESCRIPTION = 'Run the tags from your Google Tag Manager container on your live website.'

function canViewGoogleTagManager(ability: AppAbility) {
  return ability.can('list', 'google-tag-manager')
}

function canEditGoogleTagManager(ability: AppAbility) {
  return ability.can('edit', 'google-tag-manager')
}

export const Route = createFileRoute(
  '/_authenticated/$orgId/settings/integrations/google-tag-manager/'
)({
  component: GoogleTagManagerSettingsPage,
  loader: async ({ context, params }) => {
    if (!canViewGoogleTagManager(context.ability)) throw notFound()

    const isReadOnly = !canEditGoogleTagManager(context.ability)
    const isGlobalOrg = Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)

    if (isGlobalOrg) return { isReadOnly }

    await context.queryClient.ensureQueryData(
      googleTagManagerQueries.integration({ organizationId: params.orgId })
    )

    return { isReadOnly }
  },
})

function OrganizationGoogleTagManagerSettings() {
  const { orgId } = Route.useParams()
  const { isReadOnly } = useIsReadOnly()
  const { data: integration } = useSuspenseGoogleTagManagerIntegration({ organizationId: orgId })
  const updateMutation = useUpdateGoogleTagManagerIntegrationMutation()
  const removeMutation = useRemoveGoogleTagManagerIntegrationMutation()
  const form = useAppForm({
    defaultValues: {
      container_id: integration.container_id ?? '',
    },
    validators: {
      onSubmit: googleTagManagerIntegrationSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        if (isReadOnly) return undefined

        try {
          const savedIntegration = await updateMutation.mutateAsync({
            container_id: normalizeGoogleTagManagerContainerId(value.container_id),
          })
          const savedValues = { container_id: savedIntegration.container_id ?? '' }

          toast.success('Google Tag Manager connected')
          setTimeout(() => formApi.reset(savedValues))
          return undefined
        } catch (error) {
          toast.error('Could not save Google Tag Manager', {
            description: getErrorMessage(error),
          })

          return serverFieldErrors(error)
        }
      },
    },
  })

  async function removeIntegration() {
    try {
      await removeMutation.mutateAsync()
      form.reset({ container_id: '' })
      toast.success('Google Tag Manager disconnected')
    } catch (error) {
      toast.error('Could not disconnect Google Tag Manager', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <div className="max-w-3xl space-y-5">
      <AdminPageHeader icon={Tags} title="Google Tag Manager" description={PAGE_DESCRIPTION} />

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>Google Tag Manager</CardTitle>
            <Badge variant={integration.connected ? 'success' : 'outline'}>
              {integration.connected ? 'Connected' : 'Not connected'}
            </Badge>
          </div>
          <CardDescription>
            Enter the container ID from your Google Tag Manager account. It starts with GTM-.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <form.AppForm>
            <form.Form>
              <div className="space-y-5">
                <form.AppField name="container_id">
                  {(field) => (
                    <field.TextInput
                      label="Container ID"
                      placeholder="GTM-XXXXXXX"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      transformValue={(value: string) => value.toUpperCase()}
                    />
                  )}
                </form.AppField>

                <div className="space-y-3 rounded-lg border border-border/60 bg-muted/25 p-4">
                  <div>
                    <Typography variant="label" className="block">
                      Where your tags run
                    </Typography>
                    <Typography variant="supporting" className="mt-1 block">
                      Your container loads on your live website, including checkout. Website
                      previews and pages opened from private email or text links, like payment
                      requests and receipts, are left out. Only add tags from services you trust.
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="label" className="block">
                      Using Google Analytics too?
                    </Typography>
                    <Typography variant="supporting" className="mt-1 block">
                      If your container already sends visits to Google Analytics, you don't need to
                      connect Google Analytics separately. Doing both counts each visit twice.
                    </Typography>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
                  <a
                    href="https://support.google.com/tagmanager/answer/12974036"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
                  >
                    Find your container ID
                    <ExternalLink className="size-3.5" />
                  </a>

                  {!isReadOnly ? (
                    <div className="ml-auto flex items-center gap-2">
                      {integration.connected ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              type="button"
                              variant="destructive"
                              disabled={removeMutation.isPending}
                            >
                              <Trash2 className="size-4" />
                              Disconnect
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Disconnect Google Tag Manager?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Your live website will stop loading this container, so its tags will
                                stop running there. Your Google Tag Manager account and tags will
                                not be deleted.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction asChild>
                                <Button variant="destructive" onClick={removeIntegration}>
                                  Disconnect
                                </Button>
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : null}
                      <SaveBar
                        mutation={updateMutation}
                        layout="inline"
                        renderInPortal={false}
                        submitButtonText={integration.connected ? 'Save' : 'Connect'}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </form.Form>
          </form.AppForm>
        </CardContent>
      </Card>
    </div>
  )
}

export function GoogleTagManagerSettingsPage() {
  return (
    <IfGlobalOrg
      show={false}
      fallback={
        <div className="max-w-3xl space-y-5">
          <AdminPageHeader icon={Tags} title="Google Tag Manager" description={PAGE_DESCRIPTION} />
          <OrganizationContextNotice>
            Switch to the florist account that owns the Google Tag Manager connection.
          </OrganizationContextNotice>
        </div>
      }
    >
      <OrganizationGoogleTagManagerSettings />
    </IfGlobalOrg>
  )
}
